import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Eye,
  Edit2,
  Download,
  Printer,
  Calendar,
  User,
} from "lucide-react";
import ManualImageUnavailable from "@/assets/images/ManualImageUnavailable.png";

interface PrescriptionViewerModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  prescription: {
    id: number;
    number: string;
    date: string;
    status: "Approved" | "Pending" | "Rejected";
    medication: string;
    fileResponse: string;
  } | null;
  onEdit?: () => void;
  isDarkMode?: boolean;
  prescriptionImagePreview: string;
}

export function PrescriptionViewerModal({
  isOpen,
  onOpenChange,
  prescription,
  onEdit,
  isDarkMode = false,
  prescriptionImagePreview,
}: PrescriptionViewerModalProps) {
  if (!prescription) return null;

  const isValidImage = prescriptionImagePreview &&
    prescriptionImagePreview.replace(/^data:[^;]+;base64,/, "").trim().length > 0;

  // Mock prescription image URL - in a real app this would come from the prescription data
  const prescriptionImageUrl = "/placeholder.svg";

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Prescription ${prescription.number}</title>
        <style>
          html, body {
            margin: 0;
            padding: 0;
            background: #fff;
            font-family: Arial, sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          @page {
            size: A4;
            margin: 10mm;
          }

          .print-container {
            padding: 10mm 15mm;
          }

          .header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 6px;
            margin-bottom: 8px;
          }

          h1 {
            margin: 0;
            font-size: 20px;
          }

          p {
            margin: 2px 0;
            font-size: 13px;
          }

          .image-container {
            display: flex;
            justify-content: center;
            align-items: center;
            page-break-inside: avoid;
            margin-top: 10px;
          }

          img {
            max-width: 95%;
            max-height: 85vh; /* scale image to fit single A4 page height */
            height: auto;
            width: auto;
            object-fit: contain;
            border: 1px solid #ccc;
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <div class="header">
            <h1>Prescription ${prescription.number}</h1>
            <p>Date: ${new Date(prescription.date).toLocaleDateString("en-GB")} | Status: ${prescription.status}</p>
            <p>Medication: ${prescription.medication}</p>
          </div>
          <div class="image-container">
            <img src="${prescriptionImagePreview}" alt="Prescription ${prescription.number}" />
          </div>
        </div>
      </body>
    </html>
  `;

    printWindow.document.write(printContent);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const handleDownload = () => {
    // In a real app, this would download the actual prescription file
    const link = document.createElement("a");
    link.href = prescriptionImagePreview;
    link.download = `prescription-${prescription.number}.jpeg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-4xl max-h-[90vh] overflow-y-auto ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
        }`}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold">
              Prescription Details
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="flex items-center gap-1"
                title="Print Prescription"
              >
                <Printer className="w-4 h-4" />
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="flex items-center gap-1"
                title="Download Prescription"
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
              {/* {onEdit && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={onEdit}
                  className="flex items-center gap-1"
                  title="Edit Prescription"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </Button>
              )} */}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Prescription Information */}
          <div
            className={`rounded-lg border p-4 ${
              isDarkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium">
                    Prescription Number
                  </span>
                </div>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {prescription.number}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium">Date Prescribed</span>
                </div>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {prescription.date}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">Status</span>
                </div>
                <Badge
                  variant="secondary"
                  className={`text-xs ${
                    prescription.status === "Approved"
                      ? "text-green-600"
                      : prescription.status === "Pending"
                        ? "text-blue-600"
                        : prescription.status === "Rejected"
                          ? "text-orange-600"
                          : "text-red-600"
                  }`}
                >
                  {prescription.status}
                </Badge>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">Medication</span>
                </div>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {prescription.medication}
                </p>
              </div>
            </div>
          </div>

          {/* Prescription Image */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Prescription Document</h3>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                <span className="text-sm text-gray-500">
                  Click image to zoom
                </span>
              </div>
            </div>

            <div
              className={`rounded-lg border overflow-hidden ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <div className="bg-white p-4 flex items-center justify-center min-h-[500px] max-h-[700px]">
                <img
                  src={isValidImage ? prescriptionImagePreview : ManualImageUnavailable}
                  alt={`Prescription ${prescription.number}`}
                  className="max-w-full max-h-[650px] w-auto h-auto object-contain cursor-pointer transition-transform hover:scale-105"
                  // onClick={() => {
                  //   window.open(prescription.fileResponse, "_blank");
                  // }}
                />
              </div>
            </div>

            {/* <div className="text-center">
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
              >
                Click the image to view in full size
              </p>
            </div> */}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
